import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n, { applyLocaleToDocument } from '@/i18n';
import { config } from '@/config';

type Theme = 'light' | 'dark';

interface UiState {
  locale: string;
  theme: Theme;
  sidebarCollapsed: boolean;
  /** The workspace panel on board and workspace pages, remembered across visits. */
  workspacePanelCollapsed: boolean;
  notificationsOpen: boolean;
  setLocale: (locale: string) => void;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  toggleWorkspacePanel: () => void;
  toggleNotifications: (open?: boolean) => void;
}

/**
 * Client-only state: what the user is looking at, not what the server knows.
 * Server data lives in React Query - never mirror it here.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      locale: config.defaultLocale,
      theme: 'light',
      sidebarCollapsed: false,
      workspacePanelCollapsed: false,
      notificationsOpen: false,

      setLocale: (locale) => {
        void i18n.changeLanguage(locale);
        applyLocaleToDocument(locale);
        set({ locale });
      },
      setTheme: (theme) => {
        document.documentElement.dataset.theme = theme;
        set({ theme });
      },
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      toggleWorkspacePanel: () => set({ workspacePanelCollapsed: !get().workspacePanelCollapsed }),
      toggleNotifications: (open) =>
        set({ notificationsOpen: open ?? !get().notificationsOpen }),
    }),
    {
      name: 'pm-ui',
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        void i18n.changeLanguage(state.locale);
        applyLocaleToDocument(state.locale);
        document.documentElement.dataset.theme = state.theme;
      },
    },
  ),
);
